// session-failure-reasons.ts
//
// Phase 04 (F1b, R-23): pure formatters for surfacing a worker's stderr tail
// in the operator-facing evidence string — dogfood F1's root cause was
// `attempt_finished.reasons` only ever saying `missing_result_event` while
// the REAL reason (e.g. a json-schema tool crash) sat unread in the stderr
// ring buffer. No I/O, no redaction of its own — the caller MUST pass an
// already-redacted `stderrTail` (session-runner-stream-consumer.ts's
// `stderrRing` is fed through SecretRedactor before this ever sees it), and
// `evidence` goes through `retry-policy.ts`'s own `redactEvidence` a SECOND
// time downstream — belt and suspenders (Risk section, phase file).
import { redactKnownValues } from "./secret-redactor.js";

const STDERR_TAIL_MAX_CHARS = 400;

/**
 * Cắt 400 ký tự cuối, gộp về 1 dòng (xoá xuống dòng/khoảng trắng thừa) —
 * dòng ■ (run-progress-lines.ts) và evidence journal đều là text 1 dòng.
 * Rỗng/toàn khoảng trắng ⇒ `undefined` (không thêm `stderr_tail:` rỗng).
 */
export function formatStderrTailReason(
  stderrTail: string | undefined,
  secretValues: readonly string[] = [],
): string | undefined {
  if (stderrTail === undefined) return undefined;
  // Review 04a L1: che known-values (kể cả value đăng ký SAU spawn, B20) TRƯỚC
  // khi cắt 400 ký tự — cắt trước để lại mảnh secret vắt qua điểm cắt.
  const collapsed = redactKnownValues(stderrTail.replace(/\s+/g, " ").trim(), secretValues);
  if (collapsed.length === 0) return undefined;
  return collapsed.length > STDERR_TAIL_MAX_CHARS
    ? collapsed.slice(-STDERR_TAIL_MAX_CHARS)
    : collapsed;
}

/** Nối `"; stderr_tail: …"` vào evidence hiện có khi có tail để nối; không đổi evidence khi không có. */
export function appendStderrTailToEvidence(
  evidence: string,
  stderrTail: string | undefined,
  secretValues: readonly string[] = [],
): string {
  const formatted = formatStderrTailReason(stderrTail, secretValues);
  return formatted === undefined ? evidence : `${evidence}; stderr_tail: ${formatted}`;
}

const PROGRESS_LINE_STDERR_TAIL_MAX_CHARS = 120;

/**
 * Rút tối đa 120 ký tự sau `stderr_tail:` trong dòng đầu `reasons` (đã qua
 * `appendStderrTailToEvidence` + `redactEvidence` lần 2) — dùng cho dòng ■
 * (run-progress-lines.ts), ngắn hơn evidence đầy đủ trong journal.
 */
export function extractStderrTailExcerptForProgressLine(
  reasons: string[] | undefined,
): string | undefined {
  const marker = "stderr_tail: ";
  const firstReason = reasons?.[0];
  const idx = firstReason?.indexOf(marker) ?? -1;
  if (firstReason === undefined || idx < 0) return undefined;
  const start = idx + marker.length;
  return firstReason.slice(start, start + PROGRESS_LINE_STDERR_TAIL_MAX_CHARS);
}
